import * as yup from "yup";

import {
    Alert,
    Box,
    Container,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {
    EagerLocation,
    LinkAction,
    LinkRegistry,
    Location
} from "../../models";
import _, { get } from "lodash";
import {
    useMemo,
    useState
} from "react";

import { AlertMessage } from "../../lib/util/AlertMessage";
import { ExternalWindowPopUp } from "../general/popout/ExternalWindowPopUp";
import { GraphQLLinkRegistryDAOFactory } from "../../lib/linkRegistry/dao/GraphQLLinkRegistryDAOFactory";
import GraphQLLocationDAOFactory from "../../lib/location/dao/graphql/GraphQLLocationDAOFactory";
import { GraphQLSearchLocationByPropertyIdOperation } from "../../lib/location/dao/GraphQLSearchLocationByPropertyIdOperation";
import { LinkRegistryDAO } from "../../lib/linkRegistry/dao/LinkRegistryDAO";
import LoadingButton from "@mui/lab/LoadingButton";
import LocationDAO from "../../lib/location/dao/LocationDAO";
import { PossibleDuplicateLocationsListItem } from "./PossibleDuplicateLocationsList";
import { PossibleDuplicateSelector } from "../../componentsV2/duplicateItems/PossibleDuplicateSelector";
import { PrintableLinkRegistryQRList } from "./PrintableLinkRegistryQRList";
import { ProposedNewLocation } from "./ProposedNewLocation";
import { StateContext } from "../../lib/design/document/state/StateContext";
import { VariablesForm } from "./VariablesForm";
import { checkForDuplicates } from "../../componentsV2/duplicateItems/CheckForDuplicate";
import { linkRegistryFormSchema } from "../../lib/linkRegistry/schema/LinkRegistryFormSchema";
import theme from "../../assets/style/theme";
import { useCreateLocation } from "../../lib/location/hook/useCreateLocation";
import { useFormik } from "formik";

const locationDAO: LocationDAO & GraphQLSearchLocationByPropertyIdOperation = GraphQLLocationDAOFactory.getInstance();

type BatchLinkRegistryFormData = yup.InferType<typeof linkRegistryFormSchema>;

const linkRegistryDAO: LinkRegistryDAO = GraphQLLinkRegistryDAOFactory.getInstance();

export const BatchLinkRegistryForm = () => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<AlertMessage | undefined>();
    const [numberOfLinks, setNumberOfLinks] = useState<number>(1);
    const [links, setLinks] = useState<Array<LinkRegistry>>([]);
    const [showQRCodes, setShowQRCodes] = useState<boolean>(false);
    const [formState, setFormState] = useState<BatchLinkRegistryFormData>();
    const { createLocation } = useCreateLocation(StateContext.INSPECTION);
    const [possibleDuplicates, setPossibleDuplicates] = useState<EagerLocation[]>();

    const initialValues = useMemo<BatchLinkRegistryFormData>(() => ({
        linkAction: LinkAction.ASSESSMENT_FORM,
        variables: {},
        isPublished: undefined,
        shouldCreateNewLocation: false
    }), []);

    const onInitialFormComplete = async (data: BatchLinkRegistryFormData) => {
        const shouldCreateNewLocation = get(data, 'shouldCreateNewLocation', false);
        if (!shouldCreateNewLocation) {
            submitData(data, false);
            return;
        }

        const duplicates = await checkForDuplicates({
            matchers: [
                { targetKey: "name", value: get(data.variables, "locationName", "") },
            ],
            fetchAllItems: () => locationDAO.getLocationsByPropertyId(
                get(data.variables, "propertyId", "")
            ),
            itemType: "location",
        });

        if (duplicates && duplicates.length > 0) {
            setFormState(data);
            setPossibleDuplicates(duplicates);
        } else {
            submitData(data, true);
        }
    };

    const submitData = async (data: BatchLinkRegistryFormData, isNewLocation: boolean) => {
        try {
            if (isNewLocation) {
                const location = await createLocation(
                    new Location({
                        propertyId: get(data.variables, 'propertyId', ''),
                        name: get(data.variables, 'locationName', ''),
                    }),
                    false
                );
                await formik.setFieldValue("variables.locationId", location.id);
                data = { ...data, variables: { ...data.variables, locationId: location.id } };
            }
            const links = await Promise.all(
                new Array(numberOfLinks).fill(null).map(async () => {
                    return await linkRegistryDAO.create({
                        action: LinkAction.ASSESSMENT_FORM,
                        propertyId: get(data.variables, 'propertyId', undefined),
                        variables: JSON.stringify(data.variables),
                        published: data.isPublished
                    });
                })
            );
            setLinks(links);
            setShowQRCodes(true);
            setAlertMessage({
                severity: "success",
                message: "Links configured successfully"
            });
        } catch (error) {
            setAlertMessage({
                severity: "error",
                message: `Failed to create link configuration: ${(error as Error).message}`
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    const formik = useFormik<BatchLinkRegistryFormData>({
        initialValues: initialValues,
        validationSchema: linkRegistryFormSchema,
        onSubmit: onInitialFormComplete
    });

    const useNewLocation = async () => {
        if (formState) {
            submitData(formState, true);
        }
        await formik.setFieldValue("shouldCreateNewLocation", false);
        setPossibleDuplicates(undefined);
    };

    const useExistingLocation = async (location: Location) => {
        await formik.setFieldValue(
            "variables.locationName",
            get(location, 'name', '')
        );
        await formik.setFieldValue("variables.locationId", get(location, 'id', ''));
        await formik.setFieldValue("shouldCreateNewLocation", false);

        if (formState) {
            const newData = {
                ...formState.variables,
                locationId: get(location, 'id', ''),
                locationName: get(location, 'name', ''),
            };
            const newState = { ...formState, shouldCreateNewLocation: false, variables: newData };
            submitData(newState, false);
        }
        setPossibleDuplicates(undefined);
    };

    const showDuplicateDialog = useMemo(() => {
        const isNewLocation = get(formik.values, "shouldCreateNewLocation", false);
        return isNewLocation && possibleDuplicates && possibleDuplicates?.length > 0;
    }, [formik.values, possibleDuplicates]);

    return (
        <Container component="main">
            <Box
                my={2}
                display="flex"
                flexDirection="column"
                alignItems="center"
                id="form-container"
                data-cy={(links && links.length > 0) ? links[0].id : "empty"}
            >
                {!showDuplicateDialog ?
                    (<Box
                        component="form"
                        onSubmit={formik.handleSubmit}
                        noValidate
                        sx={{ mt: 1 }}
                        width="100%"
                        maxWidth={theme.breakpoints.values["md"]}
                    >
                        <Grid container spacing={2}>
                            <Grid container item xs={12} spacing={2} alignContent="flex-start">
                                <Grid item xs={12}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number"
                                        id="linksToCreate"
                                        label="Number of Links to Create"
                                        name="linksToCreate"
                                        defaultValue={numberOfLinks}
                                        onBlur={(event) => setNumberOfLinks(parseInt(event.target.value))}
                                    />
                                </Grid>
                                <VariablesForm
                                    formik={formik}
                                />
                                <Grid item xs={12}>
                                    <Typography variant="h6">Publishing</Typography>
                                    <Typography variant="body2">(If link is not enabled, scanning the QR code will link to this page for further configuration.)</Typography>
                                    <FormControl
                                        fullWidth
                                        error={formik.touched.isPublished && Boolean(formik.errors.isPublished)}
                                        sx={{ mt: 1 }}
                                    >
                                        <InputLabel id="isPublished-label">Publish Link?</InputLabel>
                                        <Select
                                            id="isPublished"
                                            labelId="isPublished-label"
                                            label="Publish Link"
                                            name="isPublished"
                                            value={formik.values.isPublished === true ? "yes" : formik.values.isPublished === false ? "no" : ""}
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                formik.setFieldValue("isPublished", value === "yes" ? true : value === "no" ? false : "");
                                            }}
                                            onBlur={formik.handleBlur}
                                            required
                                        >
                                            <MenuItem value="yes">Yes</MenuItem>
                                            <MenuItem value="no">No</MenuItem>
                                        </Select>
                                        <FormHelperText>{formik.touched.isPublished && formik.errors.isPublished}</FormHelperText>
                                    </FormControl>
                                    <FormHelperText>{formik.touched.isPublished && formik.errors.isPublished}</FormHelperText>
                                </Grid>
                            </Grid>
                            {alertMessage && (
                                <Grid item xs={12}>
                                    <Alert severity={alertMessage.severity}>{alertMessage.message}</Alert>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <LoadingButton
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    loading={isSubmitting}
                                    startIcon={<></>}
                                    loadingPosition="start"
                                    disabled={!formik.isValid}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                            {
                                showQRCodes &&
                                <ExternalWindowPopUp onClose={() => setShowQRCodes(false)}>
                                    <PrintableLinkRegistryQRList links={links} />
                                </ExternalWindowPopUp>
                            }
                        </Grid>
                    </Box>) :
                    (<PossibleDuplicateSelector<Location>
                        itemTypeDisplayString="location"
                        onUseExisting={useExistingLocation}
                        possibleDuplicates={possibleDuplicates ?? []}
                        renderPossibleDuplicateItem={(item, isCurrentSelection, onClick, key) => (
                            <PossibleDuplicateLocationsListItem key={key} item={item} isCurrentSelection={isCurrentSelection} onClick={onClick} />
                        )}
                        proposedNewItem={
                            <ProposedNewLocation
                                locationName={get(formik.values.variables, "locationName", "")}
                                returnToEditing={() => setPossibleDuplicates(undefined)}
                                useNewLocation={useNewLocation}
                            />
                        }
                    />)
                }
            </Box>
        </Container >
    );
};

